/**
 * OperatorGizmo — contextual drag handles for operator tools.
 *
 * Rendered when an operator tool is active via the tool palette:
 *
 * - **Extrude / Bevel**: arrow handle along the relevant axis
 * - **Inset Faces**: radial ring on the face plane (like a brush cursor).
 *   Drag in any direction to increase the inset amount; drag back toward
 *   the origin to decrease.  The ring grows to reflect the current amount.
 *
 * NOT shown when operators are invoked via keyboard shortcuts — those use
 * the immediate command flow instead.
 */

import { type ThreeEvent, useFrame, useThree } from "@react-three/fiber";
import { useCallback, useEffect, useMemo, useRef } from "react";
import * as THREE from "three/webgpu";
import {
  composeSceneObjectWorldMatrix,
  composeSceneObjectWorldQuaternion,
} from "../../app/collectionGraph.ts";
import { useStore } from "../../app/store.ts";
import { computeSelectionNormal } from "../../modeling/meshOps.ts";
import { computePivot } from "../../modeling/pivot.ts";
import { createRafCoalescer } from "../../rafCoalescer.ts";
import { makeScratch } from "../scratch.ts";
import { screenToWorldRadius } from "../screenRadius.ts";
import {
  DEFAULT_OPACITY,
  GIZMO_SCREEN_PX,
  OP_HANDLE_COLOR,
  OP_HANDLE_COLOR_HOVER,
} from "./constants.ts";
import {
  OP_HANDLE_HEAD,
  OP_HANDLE_SHAFT,
  OP_HIT_HANDLE,
} from "./gizmoGeometry.ts";
import { applyGizmoRenderOrder } from "./renderOrder.ts";

// ---------------------------------------------------------------------------
// Operator classification
// ---------------------------------------------------------------------------

const OPERATOR_IDS = new Set([
  "extrude",
  "extrude_along_normals",
  "extrude_manifold",
  "extrude_individual",
  "inset_faces",
  "bevel",
  "shrink_fatten",
]);

/** Operators that show an arrow handle (along normal or tangent). */
const ARROW_OPERATORS = new Set([
  "extrude",
  "extrude_along_normals",
  "extrude_manifold",
  "extrude_individual",
  "bevel",
  "shrink_fatten",
]);

/** Operators that show a radial ring indicator. */
const RING_OPERATORS = new Set(["inset_faces"]);

// ---------------------------------------------------------------------------
// Shared materials
// ---------------------------------------------------------------------------

const handleMaterial = new THREE.MeshBasicMaterial({
  color: OP_HANDLE_COLOR,
  transparent: true,
  opacity: DEFAULT_OPACITY,
  depthTest: false,
  depthWrite: false,
});

const handleHoverMaterial = new THREE.MeshBasicMaterial({
  color: OP_HANDLE_COLOR_HOVER,
  transparent: true,
  opacity: 1.0,
  depthTest: false,
  depthWrite: false,
});

const invisibleMaterial = new THREE.MeshBasicMaterial({
  visible: false,
  side: THREE.DoubleSide,
});

// ---------------------------------------------------------------------------
// Ring geometry for inset (brush-cursor style)
// ---------------------------------------------------------------------------

const RING_SEGMENTS = 48;
const RING_INNER = 0.92;
const RING_OUTER = 1.0;
const RING_IDLE_RADIUS = 0.6; // gizmo-local units at idle
const RING_HIT_RADIUS = 0.8; // hit disc radius (larger than visual)

const ringGeo = new THREE.RingGeometry(RING_INNER, RING_OUTER, RING_SEGMENTS);
const ringDiscGeo = new THREE.CircleGeometry(1, RING_SEGMENTS);
const ringHitGeo = new THREE.CircleGeometry(RING_HIT_RADIUS, RING_SEGMENTS);

const ringMaterial = new THREE.MeshBasicMaterial({
  color: OP_HANDLE_COLOR,
  transparent: true,
  opacity: DEFAULT_OPACITY,
  depthTest: false,
  depthWrite: false,
  side: THREE.DoubleSide,
});

const ringHoverMaterial = new THREE.MeshBasicMaterial({
  color: OP_HANDLE_COLOR_HOVER,
  transparent: true,
  opacity: 1.0,
  depthTest: false,
  depthWrite: false,
  side: THREE.DoubleSide,
});

// Geometry is +Y aligned — used as the "from" vector in setFromUnitVectors
const _upY = new THREE.Vector3(0, 1, 0);
// Ring geometry lies in XY plane; orient from +Z to the normal
const _upZ = new THREE.Vector3(0, 0, 1);

// Per-frame scratch — avoids allocating a new Vector3 every useFrame tick
const op = makeScratch({
  pivotWorld: "vec3",
  objectMatrix: "mat4", // object-local → world transform
  objQuat: "quat", // world rotation quaternion (for handle direction)
  pivotLocal: "vec3", // local pivot → world space
  handleDir: "vec3", // handle direction rotated to world space
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OperatorGizmo() {
  const activeOperator = useStore((s) => s.activeOperator);
  const mode = useStore((s) => s.mode);
  const editMode = useStore((s) => s.editMode);
  const editObjMap = useStore((s) => s.sceneObjects);
  const commandBuffer = useStore((s) => s.commandBuffer);
  const pivotReference = useStore((s) => s.pivotReference);

  const { camera, size: viewportSize } = useThree();

  const groupRef = useRef<THREE.Group>(null);
  const handleGroupRef = useRef<THREE.Group>(null);
  const shaftRef = useRef<THREE.Mesh>(null);
  const headRef = useRef<THREE.Mesh>(null);
  const ringRef = useRef<THREE.Mesh>(null);
  const ringDiscRef = useRef<THREE.Mesh>(null);
  const hoveredRef = useRef(false);
  const draggingRef = useRef(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  // -----------------------------------------------------------------------
  // Visibility
  // -----------------------------------------------------------------------

  const isOperatorGizmo =
    activeOperator != null && OPERATOR_IDS.has(activeOperator);
  const isArrow = activeOperator != null && ARROW_OPERATORS.has(activeOperator);
  const isRing = activeOperator != null && RING_OPERATORS.has(activeOperator);
  const inEditMode = mode === "edit" && editMode != null;
  const hasSelection = inEditMode && editMode!.selectedVertices.size > 0;
  const commandActive = commandBuffer != null;

  // Show idle gizmo when operator is toggled but no command yet;
  // keep ring visible during active inset command for live feedback
  const showIdle =
    isOperatorGizmo && inEditMode && hasSelection && !commandActive;
  const showLiveRing =
    isRing &&
    inEditMode &&
    hasSelection &&
    commandActive &&
    commandBuffer?.type === "inset";
  const visible = showIdle || showLiveRing;

  // -----------------------------------------------------------------------
  // Edit object
  // -----------------------------------------------------------------------

  const editObj = useMemo(() => {
    if (!inEditMode) return null;
    return editObjMap.get(editMode!.objectId) ?? null;
  }, [inEditMode, editMode, editObjMap]);

  // -----------------------------------------------------------------------
  // Pivot
  // -----------------------------------------------------------------------

  const pivot = useMemo((): [number, number, number] => {
    if (!inEditMode || !editObj || !editMode) return [0, 0, 0];
    const positions: [number, number, number][] = [];
    const verts = editObj.vertices;
    for (const vi of editMode.selectedVertices) {
      positions.push([verts[vi * 3], verts[vi * 3 + 1], verts[vi * 3 + 2]]);
    }
    return computePivot(positions, pivotReference);
  }, [inEditMode, editMode, editObj, pivotReference]);

  // -----------------------------------------------------------------------
  // Normal direction
  // -----------------------------------------------------------------------

  const normalDir = useMemo(() => {
    if (!inEditMode || !editObj || !editMode) return null;
    const n = computeSelectionNormal(
      editObj.vertices,
      editObj.loopNormals,
      editObj.topology,
      editMode.selectedVertices,
    );
    const len = Math.sqrt(n[0] * n[0] + n[1] * n[1] + n[2] * n[2]);
    if (len < 1e-10) return null;
    return new THREE.Vector3(n[0], n[1], n[2]).normalize();
  }, [inEditMode, editObj, editMode]);

  // For arrow operators: compute the handle direction
  const handleDir = useMemo(() => {
    if (!normalDir) return null;

    if (activeOperator === "bevel") {
      // Bevel: handle in the face plane (tangent)
      const up =
        Math.abs(normalDir.y) < 0.99
          ? new THREE.Vector3(0, 1, 0)
          : new THREE.Vector3(1, 0, 0);
      return new THREE.Vector3().crossVectors(up, normalDir).normalize();
    }

    // Extrude: handle along the normal
    return normalDir;
  }, [normalDir, activeOperator]);

  // -----------------------------------------------------------------------
  // Per-frame: position, scale, orient
  // -----------------------------------------------------------------------

  useFrame(() => {
    const group = groupRef.current;
    const handleGroup = handleGroupRef.current;
    if (!group || !handleGroup || !visible || !editMode) return;

    // World-space matrix + quaternion for the edit target. Composing
    // `collection.transform ∘ obj local` via `collectionGraph` keeps this
    // gizmo anchored to the rendered mesh when the target is a collection
    // member; `op.objQuat` then rotates the selection normal from the
    // object-local frame into world.
    const st = useStore.getState();
    const worldMat = composeSceneObjectWorldMatrix(
      st.sceneObjects,
      st.collections,
      editMode.objectId,
      op.objectMatrix,
    );
    if (!worldMat) return;
    composeSceneObjectWorldQuaternion(
      st.sceneObjects,
      st.collections,
      editMode.objectId,
      op.objQuat,
    );
    op.pivotLocal
      .set(pivot[0], pivot[1], pivot[2])
      .applyMatrix4(op.objectMatrix);
    group.position.copy(op.pivotLocal);

    // Screen-space constant size
    group.getWorldPosition(op.pivotWorld);
    group.scale.setScalar(
      screenToWorldRadius(
        GIZMO_SCREEN_PX,
        op.pivotWorld,
        camera,
        viewportSize.height,
      ),
    );

    // Handle/ring directions are in object-local space; rotate to world space.
    if (isArrow && handleDir) {
      op.handleDir.copy(handleDir).applyQuaternion(op.objQuat);
      handleGroup.quaternion.setFromUnitVectors(_upY, op.handleDir);
    } else if (isRing && normalDir) {
      op.handleDir.copy(normalDir).applyQuaternion(op.objQuat);
      handleGroup.quaternion.setFromUnitVectors(_upZ, op.handleDir);
    } else {
      handleGroup.quaternion.identity();
    }
  });

  // -----------------------------------------------------------------------
  // Ring live scaling during inset command
  // -----------------------------------------------------------------------

  useFrame(() => {
    if (!isRing || !ringRef.current) return;
    if (showLiveRing && commandBuffer) {
      // During active inset: scale ring based on mouse distance from origin.
      // The command buffer stores mouseOrigin; compute radial distance and
      // map to a visual ring scale.
      const mo = commandBuffer.mouseOrigin;
      if (mo) {
        // We don't have direct access to the current mouse position here,
        // but we can infer the inset amount from the geometry change.
        // Instead, use the ring at idle size — the mesh deformation itself
        // provides the visual feedback. Just keep the ring visible at idle.
        ringRef.current.scale.setScalar(RING_IDLE_RADIUS);
      } else {
        ringRef.current.scale.setScalar(RING_IDLE_RADIUS);
      }
    } else if (ringRef.current) {
      ringRef.current.scale.setScalar(RING_IDLE_RADIUS);
    }
    if (ringDiscRef.current) {
      ringDiscRef.current.scale.setScalar(RING_IDLE_RADIUS);
    }
  });

  // -----------------------------------------------------------------------
  // Hover material updates
  // -----------------------------------------------------------------------

  useFrame(() => {
    if (!visible) return;
    if (isArrow) {
      const mat = hoveredRef.current ? handleHoverMaterial : handleMaterial;
      if (shaftRef.current && shaftRef.current.material !== invisibleMaterial) {
        shaftRef.current.material = mat;
      }
      if (headRef.current && headRef.current.material !== invisibleMaterial) {
        headRef.current.material = mat;
      }
    } else if (isRing) {
      const mat = hoveredRef.current ? ringHoverMaterial : ringMaterial;
      if (ringRef.current) ringRef.current.material = mat;
    }
  });

  // -----------------------------------------------------------------------
  // Interaction
  // -----------------------------------------------------------------------

  const handlePointerDown = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      if (draggingRef.current) return;
      if (useStore.getState().commandBuffer) return;

      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();

      const state = useStore.getState();

      if (
        activeOperator === "extrude" ||
        activeOperator === "extrude_along_normals" ||
        activeOperator === "extrude_manifold" ||
        activeOperator === "extrude_individual"
      ) {
        // Clear extrudePending before entering the command so viewport-click
        // extrude targeting and palette-tool gizmo drags can't both fire.
        const em = state.editMode;
        if (em?.extrudePending) {
          useStore.setState({ editMode: { ...em, extrudePending: false } });
        }
        if (activeOperator === "extrude_along_normals") {
          state.extrudeAlongNormalsSelection();
        } else if (activeOperator === "extrude_manifold") {
          state.extrudeManifoldSelection();
        } else if (activeOperator === "extrude_individual") {
          state.extrudeIndividualFacesSelection();
        } else {
          state.extrudeSelection();
        }
      } else if (activeOperator === "inset_faces") {
        state.insetSelection();
      } else if (activeOperator === "bevel") {
        state.bevelSelection();
      } else if (activeOperator === "shrink_fatten") {
        state.enterCommand("shrinkFatten");
      } else if (activeOperator === "skin_radius") {
        state.enterCommand("skinRadius");
      }

      draggingRef.current = true;
      const livePreview = createRafCoalescer<{
        clientX: number;
        clientY: number;
      }>(({ clientX, clientY }) => {
        useStore.getState().applyCommandLive(clientX, clientY);
      });

      const cb = useStore.getState().commandBuffer;
      if (cb) {
        livePreview.schedule({
          clientX: e.nativeEvent.clientX,
          clientY: e.nativeEvent.clientY,
        });
        livePreview.flush();
      }

      const onMove = (ev: PointerEvent) => {
        livePreview.schedule({ clientX: ev.clientX, clientY: ev.clientY });
      };
      const onUp = () => {
        livePreview.flush();
        useStore.getState().confirmCommand();
        draggingRef.current = false;
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
        cleanupRef.current = null;
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
      cleanupRef.current = () => {
        livePreview.cancel();
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
      };
    },
    [activeOperator],
  );

  const handlePointerOver = useCallback((e: ThreeEvent<PointerEvent>) => {
    hoveredRef.current = true;
    e.stopPropagation();
  }, []);

  const handlePointerOut = useCallback((e: ThreeEvent<PointerEvent>) => {
    if (!draggingRef.current) {
      hoveredRef.current = false;
    }
    e.stopPropagation();
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      cleanupRef.current?.();
    };
  }, []);

  // Clean up if command is cancelled externally
  useEffect(() => {
    if (!commandBuffer && draggingRef.current) {
      draggingRef.current = false;
      cleanupRef.current?.();
      cleanupRef.current = null;
    }
  }, [commandBuffer]);

  useEffect(() => {
    const group = groupRef.current;
    if (!group) return;
    applyGizmoRenderOrder(group, 999);
  });

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  if (!visible) return null;

  const hitEvents = {
    onPointerDown: handlePointerDown,
    onPointerOver: handlePointerOver,
    onPointerOut: handlePointerOut,
  };

  // Gizmo lives entirely in world space — pivot is transformed to world space
  // in useFrame. The outer group is a neutral JSX container with no transform.
  return (
    <group userData={{ excludeFromExport: true }}>
      <group ref={groupRef} renderOrder={999}>
        <group ref={handleGroupRef}>
          {/* ---- Arrow handle (extrude / bevel) ---- */}
          {isArrow && (
            <>
              <mesh ref={shaftRef} geometry={OP_HANDLE_SHAFT}>
                <meshBasicMaterial
                  color={OP_HANDLE_COLOR}
                  transparent
                  opacity={DEFAULT_OPACITY}
                  depthTest={false}
                  depthWrite={false}
                />
              </mesh>
              <mesh ref={headRef} geometry={OP_HANDLE_HEAD}>
                <meshBasicMaterial
                  color={OP_HANDLE_COLOR}
                  transparent
                  opacity={DEFAULT_OPACITY}
                  depthTest={false}
                  depthWrite={false}
                />
              </mesh>
              <mesh
                geometry={OP_HIT_HANDLE}
                material={invisibleMaterial}
                userData={{ isGizmoHandle: true }}
                {...hitEvents}
              />
            </>
          )}

          {/* ---- Radial ring (inset) ---- */}
          {isRing && (
            <>
              {/* Visual ring */}
              <mesh ref={ringRef} geometry={ringGeo} scale={RING_IDLE_RADIUS}>
                <meshBasicMaterial
                  color={OP_HANDLE_COLOR}
                  transparent
                  opacity={DEFAULT_OPACITY}
                  depthTest={false}
                  depthWrite={false}
                  side={THREE.DoubleSide}
                />
              </mesh>
              {/* Translucent fill disc */}
              <mesh
                ref={ringDiscRef}
                geometry={ringDiscGeo}
                scale={RING_IDLE_RADIUS}
              >
                <meshBasicMaterial
                  color={OP_HANDLE_COLOR}
                  transparent
                  opacity={0.06}
                  depthTest={false}
                  depthWrite={false}
                  side={THREE.DoubleSide}
                />
              </mesh>
              {/* Hit disc (larger, invisible) */}
              <mesh
                geometry={ringHitGeo}
                material={invisibleMaterial}
                userData={{ isGizmoHandle: true }}
                {...hitEvents}
              />
            </>
          )}
        </group>
      </group>
    </group>
  );
}
